import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useApiMutation } from "../../../../../common/infrastructure/hooks/useApiMutation";
import { endpointsMetadata } from "../../../../../common/infrastructure/routes/endpointsMetadata";
import {
  DepositTransactionMode,
  IUserDepositTransactionFormModel,
  userDepositTransactionFormDefaultValues,
  userDepositTransactionFormSchema,
} from "../models/userDepositTransactionFormModel";

interface IUseUserDepositTransactionDialogProps {
  userId?: number;
  mode: DepositTransactionMode;
  visible: boolean;
  onHide: () => void;
}

export const useUserDepositTransactionDialog = ({
  userId,
  mode,
  visible,
  onHide,
}: IUseUserDepositTransactionDialogProps) => {
  const form = useForm<IUserDepositTransactionFormModel>({
    defaultValues: userDepositTransactionFormDefaultValues,
    resolver: yupResolver(userDepositTransactionFormSchema),
    mode: "onChange",
  });

  // One idempotency key per dialog-open: every submit of THIS form fill reuses
  // the same key, so a double-click / double-submit can never create two ledger
  // rows (a fresh key per submit would defeat the API's deduplication).
  const idempotencyKeyRef = useRef<string>(crypto.randomUUID());

  useEffect(() => {
    if (!visible) {
      form.reset(userDepositTransactionFormDefaultValues);
    } else {
      idempotencyKeyRef.current = crypto.randomUUID();
    }
  }, [visible, form]);

  const { mutate, isLoading } = useApiMutation();

  const isTopUp = mode === "topUp";
  const headerLabel = isTopUp ? "Зареждане на депозит" : "Изваждане от депозит";
  const confirmLabel = isTopUp ? "Зареди" : "Извади";

  const handleSubmit = form.handleSubmit(
    (values: IUserDepositTransactionFormModel) => {
      if (!userId) {
        return;
      }

      mutate(
        {
          endpoint: isTopUp
            ? endpointsMetadata.deposit.topUp
            : endpointsMetadata.deposit.withdraw,
          method: "post",
          globalIsLoading: true,
          // TopUp/Withdraw return Ok(DepositHistoryViewModel) — an object with
          // no resultMessage — so without this useApiMutation would toast
          // `data?.toString()`, i.e. "[object Object]".
          successMessage: isTopUp
            ? "Депозитът е зареден успешно."
            : "Сумата е извадена успешно.",
          payload: {
            userId,
            amount: values.amount,
            comment: values.comment,
            // Stable per dialog-open: the API deduplicates retries of the SAME
            // submission (double-click, network retry) by this key.
            idempotencyKey: idempotencyKeyRef.current,
          },
          invalidateKeys: [
            [endpointsMetadata.user.getUser, String(userId)],
            [endpointsMetadata.deposit.getHistory, String(userId)],
          ],
        },
        {
          onSuccess: () => {
            onHide();
          },
        },
      );
    },
  );

  return {
    form,
    isLoading,
    headerLabel,
    confirmLabel,
    handleSubmit,
  };
};
